import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Button, Card, Container, Row, Spinner, Col, Modal, FormControl, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AuthGetRequest, AuthPostRequest } from '../Backend/APIMiddleware';
import { Form } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';
import { convertToLocalTime } from '../Backend/BackgroundTasks/convertutclocal';

async function queryDocumentHistory(docType, docId, pageSize, lastItem) {
    try {
        const baseUrl = `/v1/document-history/${docType}/${docId}`;
        const queryParams = [`pageSize=${pageSize}`];
        if (lastItem !== null) queryParams.push(`lastItem=${lastItem}`);
        const url = `${baseUrl}?${queryParams.join('&')}`;
        const response = await AuthPostRequest(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching paged history:', error);
        throw error;
    }
}

const formatCurrency = (v) =>
    new Intl.NumberFormat(navigator.language || 'en-US', { style: 'currency', currency: 'USD' }).format(Number(v ?? 0));

export const CustomerDetails = (props) => {
    const { credentials, currentUser } = props;
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const customerID = queryParams.get('customerID');

    // ── Tab data ──────────────────────────────────────────────────────────────
    const [purchases, setPurchases]         = useState([]);
    const [repairs, setRepairs]             = useState([]);
    const [layaways, setLayaways]           = useState([]);
    const [accountHistory, setAccountHistory] = useState([]);
    const [lastSizes, setLastSizes]         = useState({ purchases: 0, repairs: 0, layaways: 0, accountHistory: 0 });
    const pageSize = 25;

    // ── Account data ──────────────────────────────────────────────────────────
    const [customerData, setCustomerData]             = useState({});
    const [isLoadingCustomer, setIsLoadingCustomer]   = useState(true);
    const [isLoadingPurchases, setIsLoadingPurchases] = useState(true);
    const [isLoadingRepairs, setIsLoadingRepairs]     = useState(true);
    const [isLoadingLayaways, setIsLoadingLayaways]   = useState(true);
    const [isLoadingHistory, setIsLoadingHistory]     = useState(true);

    // ── Email edit modal ──────────────────────────────────────────────────────
    const [showEditEmailModal, setShowEditEmailModal] = useState(false);
    const [newCustomerEmail, setNewCustomerEmail]     = useState('');
    const [emailError, setEmailError]                 = useState('');
    const [isSavingEmail, setIsSavingEmail]           = useState(false);

    // ── Promotions toggle ─────────────────────────────────────────────────────
    const [isSavingPromo, setIsSavingPromo] = useState(false);

    // ── Reset password modal ──────────────────────────────────────────────────
    const [showResetModal, setShowResetModal]           = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);

    // ── Card payments tab ─────────────────────────────────────────────────────
    const [customerPayments, setCustomerPayments]   = useState([]);
    const [isLoadingPayments, setIsLoadingPayments] = useState(true);

    // ── Initial data load — customer card first, all 4 tabs fire in parallel ──
    useEffect(() => {
        let isMounted = true;

        // Customer card
        setIsLoadingCustomer(true);
        AuthGetRequest(`/v1/accounts?customerID=${customerID}`)
            .then(res  => { if (isMounted) setCustomerData(res.data); })
            .catch(e   => console.error('Error fetching customer:', e))
            .finally(() => { if (isMounted) setIsLoadingCustomer(false); });

        // Purchases tab
        setIsLoadingPurchases(true);
        AuthGetRequest(`/v1/transactions/customer-search?customerId=${customerID}&ticketType=Sale&pageSize=${pageSize}&lastTransactionId=`)
            .then(res => {
                if (!isMounted) return;
                const data = res.data || [];
                setPurchases(data);
                setLastSizes(prev => ({ ...prev, purchases: data.length }));
            })
            .catch(e => console.error('Error fetching purchases:', e))
            .finally(() => { if (isMounted) setIsLoadingPurchases(false); });

        // Repairs tab
        setIsLoadingRepairs(true);
        AuthGetRequest(`/v1/transactions/customer-search?customerId=${customerID}&ticketType=Repair&pageSize=${pageSize}&lastTransactionId=`)
            .then(res => {
                if (!isMounted) return;
                const data = res.data || [];
                setRepairs(data);
                setLastSizes(prev => ({ ...prev, repairs: data.length }));
            })
            .catch(e => console.error('Error fetching repairs:', e))
            .finally(() => { if (isMounted) setIsLoadingRepairs(false); });

        // Layaways tab
        setIsLoadingLayaways(true);
        AuthGetRequest(`/v1/transactions/customer-search?customerId=${customerID}&ticketType=Layaway&pageSize=${pageSize}&lastTransactionId=`)
            .then(res => {
                if (!isMounted) return;
                const data = res.data || [];
                setLayaways(data);
                setLastSizes(prev => ({ ...prev, layaways: data.length }));
            })
            .catch(e => console.error('Error fetching layaways:', e))
            .finally(() => { if (isMounted) setIsLoadingLayaways(false); });

        // Account history tab
        setIsLoadingHistory(true);
        queryDocumentHistory('account', customerID, pageSize, null)
            .then(data => {
                if (!isMounted) return;
                const rows = data || [];
                setAccountHistory(rows);
                setLastSizes(prev => ({ ...prev, accountHistory: rows.length }));
            })
            .catch(e => console.error('Error fetching account history:', e))
            .finally(() => { if (isMounted) setIsLoadingHistory(false); });

        // Card payments tab
        setIsLoadingPayments(true);
        AuthGetRequest(`/v1/accounts/payments?customerId=${customerID}`)
            .then(res => { if (isMounted) setCustomerPayments(res.data || []); })
            .catch(e => console.error('Error fetching payments:', e))
            .finally(() => { if (isMounted) setIsLoadingPayments(false); });

        return () => { isMounted = false; };
    }, [customerID]);

    const fetchMoreData = async (type) => {
        try {
            if (type === 'accountHistory') {
                const lastId = accountHistory.length ? accountHistory[accountHistory.length - 1].id : null;
                const response = await queryDocumentHistory('account', customerID, pageSize, lastId);
                setLastSizes(prev => ({ ...prev, accountHistory: response.length }));
                if (response.length > 0) setAccountHistory(prev => [...prev, ...response]);
            } else if (type === 'layaways') {
                // Layaways are fetched all at once (typically a small set)
                const apiEndpoint = `/v1/transactions/customer-search?customerId=${customerID}&ticketType=Layaway&pageSize=${pageSize}&lastTransactionId=`;
                const response = await AuthGetRequest(apiEndpoint);
                setLastSizes(prev => ({ ...prev, layaways: response.data.length }));
                setLayaways(response.data);  // Replace, not append (all at once)
            } else {
                let lastTransactionId = '';
                let apiEndpoint = '';
                switch (type) {
                    case 'purchases':
                        lastTransactionId = purchases.length ? purchases[purchases.length - 1].transactionId : '';
                        apiEndpoint = `/v1/transactions/customer-search?customerId=${customerID}&ticketType=Sale&pageSize=${pageSize}&lastTransactionId=${lastTransactionId}`;
                        break;
                    case 'repairs':
                        lastTransactionId = repairs.length ? repairs[repairs.length - 1].transactionId : '';
                        apiEndpoint = `/v1/transactions/customer-search?customerId=${customerID}&ticketType=Repair&pageSize=${pageSize}&lastTransactionId=${lastTransactionId}`;
                        break;
                    default:
                        return;
                }
                const response = await AuthGetRequest(apiEndpoint);
                setLastSizes(prev => ({ ...prev, [type]: response.data.length }));
                if (response.data.length > 0) {
                    if (type === 'purchases') setPurchases(prev => [...prev, ...response.data]);
                    if (type === 'repairs')   setRepairs(prev => [...prev, ...response.data]);
                }
            }
        } catch (error) {
            console.error(`Error fetching more ${type}:`, error);
        }
    };

    // ── Email ─────────────────────────────────────────────────────────────────
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleEditEmail = async () => {
        if (!validateEmail(newCustomerEmail)) {
            setEmailError(t('Please enter a valid email address'));
            return;
        }
        setEmailError('');
        setIsSavingEmail(true);
        try {
            await AuthPostRequest(`/v1/accounts/update?customerID=${customerID}`, { email: newCustomerEmail });
            setCustomerData(prev => ({ ...prev, email: newCustomerEmail }));
            setShowEditEmailModal(false);
        } catch (error) {
            setEmailError(t('Failed to update email. Please try again.'));
            console.error('Error updating email:', error);
        } finally {
            setIsSavingEmail(false);
        }
    };

    // ── Promotions toggle ─────────────────────────────────────────────────────
    const handlePromotionsToggle = async (enabled) => {
        setIsSavingPromo(true);
        try {
            await AuthPostRequest(`/v1/accounts/update?customerID=${customerID}`, { promotionalEmails: enabled });
            setCustomerData(prev => ({ ...prev, emailPromotions: enabled }));
        } catch (error) {
            console.error('Error updating promotions:', error);
        } finally {
            setIsSavingPromo(false);
        }
    };

    // ── Reset password ────────────────────────────────────────────────────────
    const handleResetPassword = async () => {
        setIsResettingPassword(true);
        try {
            await AuthPostRequest(`/v1/accounts/reset-password?customerID=${customerID}`);
            setShowResetModal(false);
            alert(t('Temporary password sent via text message.'));
        } catch (error) {
            const msg = error?.response?.data || '';
            if (msg.includes('No phone')) {
                alert(t('No phone number on file for this customer.'));
            } else {
                alert(t('Failed to reset password. Please try again.'));
            }
            console.error('Error resetting password:', error);
        } finally {
            setIsResettingPassword(false);
        }
    };

    // ── Navigation ────────────────────────────────────────────────────────────
    const goToTransactionDetails = (transactionId, transactionType) => {
        navigate(`/transactions/${transactionType}/${transactionId}`);
    };

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderCard = (item, type) => (
        <Card className="mb-3" key={item.transactionId || item.id}>
            <Card.Header>
                <Row>
                    <Col><strong>{t(type === 'accountHistory' ? 'Event' : 'Transaction #')}:</strong><br />
                        {type === 'accountHistory' ? item.logType : item.ticketId}</Col>
                    {type !== 'accountHistory' && (
                        <Col className="text-end"><strong>{t('Total')}:</strong> ${item.transactionTotal}</Col>
                    )}
                </Row>
            </Card.Header>
            <Card.Body>
                <Card.Text>
                    {type === 'accountHistory' ? (
                        <>
                            <strong>{t('Date')}:</strong> {convertToLocalTime(item.logDate)}<br />
                            <strong>{t('Employee')}:</strong> {item.employeeName || t('N/A')}<br />
                            <strong>{t('Details')}:</strong> {item.logMessage || t('N/A')}<br />
                        </>
                    ) : (
                        <>
                            <strong>{t('Transaction Date')}:</strong> {convertToLocalTime(item.transactionDate)}<br />
                            <strong>{t('Employee')}:</strong> {item.employeeName}<br />
                            <strong>{t('Balance Due')}:</strong> {formatCurrency(item.balanceDue)}<br />
                            {type === 'layaway' && (
                                <>
                                    <strong>{t('Installment')}:</strong> {formatCurrency(item.layawayInstallmentAmount)} / {item.layawayPaymentPlan || t('N/A')}<br />
                                    <strong>{t('Maturity Date')}:</strong> {item.layawayMaturityDate ? convertToLocalTime(item.layawayMaturityDate) : t('N/A')}<br />
                                </>
                            )}
                            {type !== 'layaway' && (
                                <><strong>{t('Total Items')}:</strong> {item.totalItems}<br /></>
                            )}
                        </>
                    )}
                </Card.Text>
                {type !== 'accountHistory' && (
                    <Button variant="primary" onClick={() => goToTransactionDetails(item.transactionId, type === 'layaway' ? 'sale' : type)}>
                        {t('View Details')}
                    </Button>
                )}
            </Card.Body>
            {type !== 'accountHistory' && (
                <Card.Footer>
                    <Row>
                        <Col><strong>{t('Status')}:</strong><br /> {item.transactionStatus || 'Active'}</Col>
                        <Col className="text-end"><strong>{t('Status Updated')}:</strong><br /> {convertToLocalTime(item.statusDate || 'N/A')}</Col>
                    </Row>
                </Card.Footer>
            )}
        </Card>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Container fluid>
            {/* ── Account card ──────────────────────────────────────────────── */}
            <Card className="mb-3">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                            <h4 className="mb-0">
                                {customerData.firstName && `${customerData.firstName} ${customerData.lastName}`}
                            </h4>
                        </Col>
                        <Col className="text-end d-flex gap-2 justify-content-end flex-wrap">
                            
                            {credentials.isEmployee && (
                                <Button variant="secondary" size="sm" onClick={() => setShowResetModal(true)}>
                                    {t('Reset Password')}
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {isLoadingCustomer ? (
                        <div className="d-flex justify-content-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Row>
                            <Col md={8}>
                                <Card.Text>
                                    <strong>{t('Account name')}:</strong> {customerData.firstName && `${customerData.firstName} ${customerData.lastName}`}<br />
                                    <strong>{t('Account Type')}:</strong> {customerData.isBusiness ? t('Business') : t('Customer')}<br />
                                    <strong>{t('Phone Number')}:</strong> {customerData.phone}<br />
                                    <strong>{t('Email')}:</strong> {customerData.email || 'Not on File'}{' '}
                                    {(credentials.isEmployee || customerID === currentUser?.userId) && (
                                        <FaEdit style={{ cursor: 'pointer' }} onClick={() => { setNewCustomerEmail(customerData.email || ''); setShowEditEmailModal(true); }} />
                                    )}<br />
                                    <Form.Check
                                        type="switch"
                                        id="promo-switch"
                                        label={<strong>{t('Email Promotions')}</strong>}
                                        disabled={
                                            isSavingPromo ||
                                            !customerData.email ||
                                            (!credentials.isEmployee && customerID !== currentUser?.userId)
                                        }
                                        checked={!!customerData.emailPromotions}
                                        onChange={(e) => handlePromotionsToggle(e.target.checked)}
                                    />
                                    {isSavingPromo && <small className="text-muted">{t('Saving…')}</small>}
                                </Card.Text>
                            </Col>

                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* ── Tabs ──────────────────────────────────────────────────────── */}
            <Tabs defaultActiveKey="purchases" id="customer-details-tabs" className="mb-3">

                <Tab eventKey="purchases" title={t('Purchases')}>
                    {isLoadingPurchases ? (
                        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            {purchases.map((p) => renderCard(p, 'sale'))}
                            {lastSizes.purchases === pageSize && (
                                <Button variant="primary" onClick={() => fetchMoreData('purchases')}>{t('Load More')}</Button>
                            )}
                            {lastSizes.purchases !== pageSize && <p className="text-muted">{t('No more purchases')}</p>}
                        </>
                    )}
                </Tab>

                <Tab eventKey="repairs" title={t('Repairs')}>
                    {isLoadingRepairs ? (
                        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            {repairs.map((r) => renderCard(r, 'repair'))}
                            {lastSizes.repairs === pageSize && (
                                <Button variant="primary" onClick={() => fetchMoreData('repairs')}>{t('Load More')}</Button>
                            )}
                            {lastSizes.repairs !== pageSize && <p className="text-muted">{t('No more repairs')}</p>}
                        </>
                    )}
                </Tab>

                <Tab eventKey="layaways" title={t('Layaway Contracts')}>
                    {isLoadingLayaways ? (
                        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            {layaways.length === 0 && <p className="text-muted">{t('No layaway contracts on file.')}</p>}
                            {layaways.map((l) => renderCard(l, 'layaway'))}
                        </>
                    )}
                </Tab>

                <Tab eventKey="cardPayments" title={`💳 ${t('Payments')}`}>
                    {isLoadingPayments ? (
                        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
                    ) : customerPayments.length === 0 ? (
                        <p className="text-muted">{t('No payments on file yet.')}</p>
                    ) : (
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>{t('Date')}</th>
                                    <th>{t('Method')}</th>
                                    <th>{t('Card')}</th>
                                    <th>{t('Transaction')}</th>
                                    <th className="text-end">{t('Amount')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customerPayments.map((p) => (
                                    <tr key={p.paymentId}>
                                        <td>{p.paymentDate ? convertToLocalTime(p.paymentDate) : '—'}</td>
                                        <td>{p.paymentMethod || '—'}</td>
                                        <td>
                                            {p.cardType || p.lastFour
                                                ? `${p.cardType ? p.cardType.charAt(0).toUpperCase() + p.cardType.slice(1) : ''}${p.lastFour ? ` •••• ${p.lastFour}` : ''}`
                                                : '—'}
                                        </td>
                                        <td>
                                            {p.transactionId ? (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0"
                                                    onClick={() => goToTransactionDetails(p.transactionId, p.transactionType || 'sale')}
                                                >
                                                    {t('View')}
                                                </Button>
                                            ) : '—'}
                                        </td>
                                        <td className="text-end fw-semibold">{formatCurrency(p.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Tab>

                <Tab eventKey="accountHistory" title={t('Account History')}>
                    {isLoadingHistory ? (
                        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
                    ) : (
                        <>
                            {accountHistory.map((h) => renderCard(h, 'accountHistory'))}
                            {lastSizes.accountHistory === pageSize && (
                                <Button variant="primary" onClick={() => fetchMoreData('accountHistory')}>{t('Load More')}</Button>
                            )}
                            {lastSizes.accountHistory !== pageSize && <p className="text-muted">{t('No more history')}</p>}
                        </>
                    )}
                </Tab>
            </Tabs>

            {/* ── Reset password modal ───────────────────────────────────────── */}
            <Modal show={showResetModal} onHide={() => setShowResetModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('Reset Password')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isResettingPassword ? (
                        <div className="text-center"><Spinner animation="border" /></div>
                    ) : (
                        <p>{t('A temporary password will be generated and sent to the customer\'s phone number via text message. They will be required to change it on next login.')}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowResetModal(false)} disabled={isResettingPassword}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleResetPassword} disabled={isResettingPassword}>
                        {isResettingPassword
                            ? <><Spinner as="span" animation="border" size="sm" className="me-1" />{t('Sending…')}</>
                            : t('Send Temporary Password')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Edit email modal ───────────────────────────────────────────── */}
            <Modal show={showEditEmailModal} onHide={() => setShowEditEmailModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('Edit Email')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputGroup>
                        <FormControl
                            placeholder={t('Enter new email')}
                            value={newCustomerEmail}
                            onChange={(e) => setNewCustomerEmail(e.target.value)}
                            isInvalid={!!emailError}
                        />
                        <FormControl.Feedback type="invalid">{emailError}</FormControl.Feedback>
                    </InputGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditEmailModal(false)} disabled={isSavingEmail}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleEditEmail} disabled={isSavingEmail}>
                        {isSavingEmail
                            ? <><Spinner as="span" animation="border" size="sm" className="me-1" />{t('Saving…')}</>
                            : t('Save')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};
